/** Row keys related through a block's row graph, each key's set computed once. */
function closure(edges: ReadonlyMap<number, number[]>): (key: number) => Set<number> {
    const known = new Map<number, Set<number>>();
    const walk = (key: number): Set<number> => {
        const cached = known.get(key);
        if (cached) return cached;
        const reached = new Set<number>();
        known.set(key, reached);
        const stack = [...(edges.get(key) ?? [])];
        while (stack.length > 0) {
            const next = stack.pop();
            if (next === undefined || reached.has(next) || next === key) continue;
            reached.add(next);
            stack.push(...(edges.get(next) ?? []));
        }
        return reached;
    };
    return walk;
}

function childrenOf(parentRows: ReadonlyMap<number, number[]>): Map<number, number[]> {
    const children = new Map<number, number[]>();
    for (const [child, parents] of parentRows) {
        for (const parent of parents)
            children.set(parent, [...(children.get(parent) ?? []), child]);
    }
    return children;
}

export interface LineageVisibility {
    /** Rows hidden because the rows whose origins were collapsed are their only descendants. */
    hidden: ReadonlySet<number>;
    /** For each collapsed row: its earlier rows kept because another shown row descends from them. */
    shared: ReadonlyMap<number, number>;
}

const NONE: ReadonlySet<number> = new Set();

/**
 * Which earlier rows a block hides once the origins of `collapsed` rows are collapsed. An
 * earlier row is hidden only when no other shown row descends from it too: in a lineage that
 * branches and joins (a redistribution), ancestors another group still needs stay on screen,
 * and are counted as shared. A `live` row (one ending in a group still in the water) is never
 * history: it stays, and so does everything it came from.
 */
export function lineageVisibility(
    parentRows: ReadonlyMap<number, number[]>,
    collapsed: ReadonlySet<number>,
    live: ReadonlySet<number> = NONE
): LineageVisibility {
    const ancestorsOf = closure(parentRows);
    const keys = [...parentRows.keys()];
    const targets = keys.filter((key) => collapsed.has(key));
    const candidates = new Set(
        targets.flatMap((key) => [...ancestorsOf(key)]).filter((key) => !live.has(key))
    );
    const kept = new Set(
        keys
            .filter((key) => !candidates.has(key) && !collapsed.has(key))
            .flatMap((key) => [...ancestorsOf(key)])
    );
    const hidden = new Set([...candidates].filter((key) => !kept.has(key)));
    const shared = new Map(
        targets.map((key) => [
            key,
            [...ancestorsOf(key)].filter((a) => kept.has(a) || live.has(a)).length,
        ])
    );
    return { hidden, shared };
}

/** A row, everything it came from and everything that came from it. */
export function lineageOf(parentRows: ReadonlyMap<number, number[]>, key: number): Set<number> {
    const ancestors = closure(parentRows)(key);
    const descendants = closure(childrenOf(parentRows))(key);
    return new Set([key, ...ancestors, ...descendants]);
}

/** The rows collapsing `key`'s origins would hide on top of those hidden already. */
export function rowsHiddenBy(
    parentRows: ReadonlyMap<number, number[]>,
    collapsed: ReadonlySet<number>,
    key: number,
    live: ReadonlySet<number> = NONE
): number[] {
    const before = lineageVisibility(parentRows, collapsed, live).hidden;
    const after = lineageVisibility(parentRows, new Set([...collapsed, key]), live).hidden;
    return [...after].filter((row) => !before.has(row));
}

/** Whether collapsing `key`'s origins would hide any row not hidden already. */
export function canHideOrigins(
    parentRows: ReadonlyMap<number, number[]>,
    collapsed: ReadonlySet<number>,
    key: number,
    live: ReadonlySet<number> = NONE
): boolean {
    return rowsHiddenBy(parentRows, collapsed, key, live).length > 0;
}
